import {
  AppPage,
  MappedAppPages,
} from '@hellodarwin/core/lib/features/entities/pages-entities';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { lazy, useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useClientApi } from '../features/api/use-client-api';
import { canAccessPlanSelection } from '../components/plan-selection/access';
import showErrorNotification from '../features/utils/show-error-notifications';
import ContactClientPage from './contact';
import ClientProjectsPage from './game-plan';
import ClientProjectPage from './game-plan/project';
import ClientNotificationsPage from './notifications';
import ClientSingleApplicationPage from './program-pages/client-single-application-page';
import ProgramPdfViewerPage from './program-pdf-viewer';
import PromoCheckoutRedirectPage from './promo-checkout';

const ClientAiIntegrationPage = lazy(() => import('./ai-integration'));
const ClientVaultPage = lazy(() => import('./client-vault'));
const ClientHomePage = lazy(() => import('./dashboard'));
const FAQClientPage = lazy(() => import('./faq'));
const ClientAssistantPage = lazy(() => import('./assistant-page'));
const ClientFundingExplorer = lazy(
  () => import('./funding-explorer/client-funding-explorer-page'),
);
const ClientFundingExplorerSingleGrantPage = lazy(
  () =>
    import(
      './funding-explorer/single-grant/client-funding-explorer-single-grant'
    ),
);
const ClientOngoingProgramsPage = lazy(
  () =>
    import('./program-pages/client-programs-page/client-roadmap-programs-page'),
);
const ClientResourcesPage = lazy(() => import('./resources'));
const ClientArticleSinglePage = lazy(() => import('./resources/[articleId]'));
const ClientReviewsPage = lazy(() => import('./reviews'));
const ClientRfpPage = lazy(() => import('./rfp'));
const ClientSingleRfpPage = lazy(() => import('./rfp/[rfpId]'));
const ClientSingleProviderPage = lazy(
  () => import('./rfp/[rfpId]/providers/[matchId]'),
);
const ClientSettingsPage = lazy(() => import('./settings'));
const ClientFundingIntelligencePage = lazy(
  () => import('./funding-intelligence'),
);
const ClientFundingIntelligenceAcceptedApplicationsPage = lazy(
  () => import('./funding-intelligence/accepted-applications'),
);
const ClientProviderListingPage = lazy(() => import('./providers'));
const ClientProviderProfilePage = lazy(
  () => import('./providers/client-provider-profile-page'),
);

const ClientPlansSettingsPage = () => {
  const { accessLevel } = usePremiumAccess();

  if (!canAccessPlanSelection(accessLevel)) {
    return <Navigate to="/profile/contact" replace />;
  }

  return <ClientSettingsPage defaultActiveKey="plans" />;
};

const Redirect = ({ to }: { to: string }) => {
  const params = useParams();

  let finalTo = to;

  if (params) {
    Object.keys(params).forEach((paramName) => {
      // Use regex to replace `:paramName` in the `to` string
      finalTo = finalTo.replace(`:${paramName}`, params[paramName] || '');
    });
  }

  return <Navigate to={finalTo} />;
};

const transformChildren = (
  children: AppPage['children'],
  oldSegment: string,
  newSegment: string,
): AppPage['children'] => {
  if (!children) return undefined;

  return Object.entries(children).reduce((acc, [childPath, childPage]) => {
    const transformedPath = childPath.replace(oldSegment, newSegment);
    const transformPage: AppPage = {
      ...childPage,
      pathname: childPage.pathname.replace(oldSegment, newSegment),
      children: transformChildren(childPage.children, oldSegment, newSegment),
    };
    return {
      ...acc,
      [transformedPath]: transformPage,
    };
  }, {} as MappedAppPages);
};

const replaceSegment = (path: string, oldSegment: string, newSegment: string) =>
  path.replace(new RegExp(`(^|/)${oldSegment}(/|$)`), `$1${newSegment}$2`);

const transformChildrenIntoRedirect = (
  children: AppPage['children'],
  oldSegment: string,
  newSegment: string,
): AppPage['children'] => {
  if (!children) return undefined;

  return Object.entries(children).reduce((acc, [childPath, childPage]) => {
    const transformedPath = childPage.pathname.replace(oldSegment, newSegment);
    const transformPage: AppPage = {
      ...childPage,
      component: <Redirect to={transformedPath} />,
      children: transformChildrenIntoRedirect(
        childPage.children,
        oldSegment,
        newSegment,
      ),
    };
    return {
      ...acc,
      [childPath]: transformPage,
    };
  }, {} as MappedAppPages);
};

const BillingRedirect = () => {
  const api = useClientApi();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const session = await api.fetchBillingSession(
          window?.location?.origin || '',
        );
        window.location.href = session.url;
      } catch (err: any) {
        showErrorNotification(err.response.data.error_code);
        navigate('/');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};

const oldProjectsPages: MappedAppPages = {
  '/projects': {
    pathname: '/projects',
    title: 'Projects',
    component: <ClientRfpPage />,
    children: {
      ':rfpId': {
        pathname: '/projects/:rfpId',
        title: 'Single Project',
        component: <ClientSingleRfpPage defaultTab="overview" />,
        children: {
          project: {
            pathname: '/projects/:rfpId/project',
            title: 'Redirect to Brief',
            component: <Redirect to="/rfp/:rfpId/brief" />,
          },
          brief: {
            pathname: '/projects/:rfpId/brief',
            title: 'Brief',
            component: <ClientSingleRfpPage defaultTab="brief" />,
          },
          matches: {
            pathname: '/projects/:rfpId/matches',
            title: 'Redirect to Candidates',
            component: <Redirect to="/rfp/:rfpId/candidates" />,
          },
          candidates: {
            pathname: '/projects/:rfpId/candidates',
            title: 'Candidates',
            component: <ClientSingleRfpPage defaultTab="candidates" />,
          },
          messages: {
            pathname: '/projects/:rfpId/messages',
            title: 'Messages',
            component: <ClientSingleRfpPage defaultTab="messages" />,
          },
          'providers/:matchId': {
            pathname: '/projects/:rfpId/providers/:matchId',
            title: 'Single Provider',
            component: <ClientSingleProviderPage />,
          },
        },
      },
    },
  },
};

const projectsRedirects = Object.entries(oldProjectsPages).reduce(
  (acc, [pathname, page]) => {
    const newPathname = replaceSegment(pathname, 'projects', 'rfp');
    const newPage: AppPage = {
      ...page,
      pathname: replaceSegment(page.pathname, 'projects', 'rfp'),
      children: transformChildren(page.children, 'projects', 'rfp'),
    };

    const oldPage: AppPage = {
      ...page,
      title: `Redirect to ${newPathname}`,
      component: <Redirect to={newPathname} />,
      children: transformChildrenIntoRedirect(page.children, 'projects', 'rfp'),
    };

    return { ...acc, [pathname]: oldPage, [newPathname]: newPage };
  },
  {} as MappedAppPages,
);

const appPages: MappedAppPages = {
  '/dashboard': {
    pathname: '/dashboard',
    title: 'Dashboard',
    component: <ClientHomePage />,
  },
  ...projectsRedirects,
  '/billing': {
    pathname: '/billing',
    title: 'Billing',
    component: <BillingRedirect />,
  },
  '/resources': {
    pathname: '/resources',
    title: 'Resources',
    component: <ClientResourcesPage />,
    children: {
      ':articleId': {
        pathname: '/resources/:articleId',
        title: 'Redirect to Resources',
        component: <Redirect to="/resources" />,
      },
    },
  },
  '/articles': {
    pathname: '/articles',
    title: 'Articles',
    component: <Redirect to="/resources" />,
    children: {
      ':articleId': {
        pathname: '/articles/:articleId',
        title: 'Single Article',
        component: <ClientArticleSinglePage />,
      },
    },
  },
  '/reviews': {
    pathname: '/reviews',
    title: 'Reviews',
    component: <ClientReviewsPage />,
  },
  '/profile': {
    pathname: '/profile',
    title: 'Profile',
    component: <Navigate to="/profile/contact" />,
    children: {
      contact: {
        pathname: '/profile/contact',
        title: 'Contact',
        component: <ClientSettingsPage defaultActiveKey="contact" />,
      },
      company: {
        pathname: '/profile/company',
        title: 'Company',
        component: <ClientSettingsPage defaultActiveKey="company" />,
      },
      users: {
        pathname: '/profile/users',
        title: 'Users',
        component: <ClientSettingsPage defaultActiveKey="users" />,
      },
      alerts: {
        pathname: '/profile/alerts',
        title: 'Alert Preferences',
        component: <ClientSettingsPage defaultActiveKey="alerts" />,
      },
      plans: {
        pathname: '/profile/plans',
        title: 'Plans',
        component: <ClientPlansSettingsPage />,
      },
    },
  },
  '/faq': {
    pathname: '/faq',
    title: 'FAQ',
    component: <FAQClientPage />,
  },
  '/contact-us': {
    pathname: '/contact-us',
    title: 'Contact',
    component: <ContactClientPage />,
  },
  '/programs': {
    pathname: '/programs',
    title: 'Programs',
    component: <Redirect to="/applications" />,
    children: {
      roadmap: {
        pathname: '/programs/roadmap',
        title: 'Roadmap',
        component: <Redirect to="/applications" />,
      },
      all: {
        pathname: '/programs/all',
        title: 'All Programs',
        component: <Redirect to="/applications" />,
      },
      'digital-transformations': {
        pathname: '/programs/digital-transformations',
        title: 'Digital Transformations',
        component: <Redirect to="/applications" />,
      },
      ':programId': {
        pathname: '/programs/:programId',
        title: 'Single Program',
        component: <Redirect to="/applications/:programId" />,
        children: {
          program: {
            pathname: '/programs/:programId/program',
            title: 'Single Program',
            component: <Redirect to="/applications/:programId/program" />,
          },
          files: {
            pathname: '/programs/:programId/files',
            title: 'Program Files',
            component: <Redirect to="/applications/:programId/files" />,
          },
        },
      },
    },
  },
  '/applications': {
    pathname: '/applications',
    title: 'Applications',
    component: <ClientOngoingProgramsPage />,
    children: {
      roadmap: {
        pathname: '/applications/roadmap',
        title: 'Roadmap',
        requiredAccess: 'premium',
        component: <Navigate to="/applications" />,
      },
      all: {
        pathname: '/applications/all',
        title: 'All Applications',
        requiredAccess: 'premium',
        component: <Navigate to="/applications" />,
      },
      'digital-transformations': {
        pathname: '/applications/digital-transformations',
        title: 'Digital Transformations',
        requiredAccess: 'premium',
        component: <Navigate to="/applications" />,
      },
      ':programId': {
        pathname: '/applications/:programId',
        title: 'Application Dashboard',
        component: <ClientSingleApplicationPage />,
        children: {
          program: {
            pathname: '/applications/:programId/program',
            title: 'Single Program',
            requiredAccess: 'premium',
            component: <Redirect to="/applications/:programId" />,
          },
          files: {
            pathname: '/applications/:programId/files',
            title: 'Program Files',
            requiredAccess: 'premium',
            component: <Redirect to="/applications/:programId" />,
          },
          pdf: {
            pathname: '/applications/:programId/pdf/:assetId',
            title: 'PDF Viewer',
            requiredAccess: 'premium',
            component: <ProgramPdfViewerPage />,
          },
        },
      },
    },
  },
  '/funding-explorer': {
    pathname: '/funding-explorer',
    title: 'Funding Explorer',
    component: <ClientFundingExplorer />,
    children: {
      history: {
        pathname: '/funding-explorer/history',
        title: 'Viewed Programs',
        component: <ClientFundingExplorer />,
      },
      ':id': {
        pathname: '/funding-explorer/:id',
        title: 'Single Grant',
        naturalScroll: true,
        component: <ClientFundingExplorerSingleGrantPage />,
      },
    },
  },
  '/vault': {
    pathname: '/vault',
    title: 'Document Vault',
    component: <ClientVaultPage />,
    requiredAccess: 'custom',
  },
  '/game-plan': {
    pathname: '/game-plan',
    title: 'Game Plan',
    component: <ClientProjectsPage />,
    requiredAccess: 'custom',
    children: {
      ':projectId': {
        pathname: '/game-plan/:projectId',
        title: 'Single Project',
        component: <ClientProjectPage />,
        children: {},
      },
    },
  },
  '/alerts': {
    pathname: '/alerts',
    title: 'Alerts',
    requiredAccess: 'premium',
    component: <ClientNotificationsPage />,
  },
  '/notifications': {
    pathname: '/notifications',
    title: 'Notifications',
    requiredAccess: 'premium',
    component: <Redirect to="/alerts" />,
  },
  '/assistant': {
    pathname: '/assistant',
    title: 'Assistant',
    requiredAccess: 'premium',
    component: <ClientAssistantPage />,
    children: {
      ':conversationId': {
        pathname: '/assistant/:conversationId',
        title: 'Assistant',
        requiredAccess: 'premium',
        component: <ClientAssistantPage />,
      },
    },
  },
  '/ai-integration': {
    pathname: '/ai-integration',
    title: 'AI Integration',
    requiredAccess: 'premium',
    component: <ClientAiIntegrationPage />,
  },
  '/signup': {
    pathname: '/signup',
    title: 'Signup',
    component: <Navigate to="/dashboard" />,
  },
  '/promo-checkout': {
    pathname: '/promo-checkout',
    title: 'Promo Checkout',
    component: <PromoCheckoutRedirectPage />,
  },
  '/funding-intelligence': {
    pathname: '/funding-intelligence',
    title: 'Funding Intelligence',
    requiredAccess: 'premium',
    component: <ClientFundingIntelligencePage />,
    children: {
      ':grantId': {
        pathname: '/funding-intelligence/:grantId',
        title: 'Accepted Applications',
        requiredAccess: 'premium',
        component: <ClientFundingIntelligenceAcceptedApplicationsPage />,
      },
    },
  },
  '/providers': {
    pathname: '/providers',
    title: 'Service Providers',
    component: <ClientProviderListingPage />,
    children: {
      ':providerId': {
        pathname: '/providers/:providerId',
        title: 'Provider Profile',
        component: <ClientProviderProfilePage />,
      },
    },
  },
};

export default appPages;
